import { useTranslation } from 'react-i18next';
import {
  Upload,
  FolderPlus,
  List,
  LayoutGrid,
  Search,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useToast } from '@/shared/hooks/useToast';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import type { ProjectFilesViewMode, FileSortField, FileSortDir } from '../types';

interface ProjectFilesToolbarProps {
  breadcrumbLabel: string;
  onNavigateRoot: () => void;
  search: string;
  onSearch: (value: string) => void;
  viewMode: ProjectFilesViewMode;
  onViewModeChange: (mode: ProjectFilesViewMode) => void;
  sortBy: FileSortField;
  sortDir: FileSortDir;
  onSortByChange: (field: FileSortField) => void;
  onSortDirToggle: () => void;
  onUpload: (file: File) => void;
  onNewFolder: () => void;
  isUploading: boolean;
  isInsideFolder: boolean;
}

const SORT_FIELDS: { value: FileSortField; labelKey: string }[] = [
  { value: 'name', labelKey: 'toolbar.sortByName' },
  { value: 'size', labelKey: 'toolbar.sortBySize' },
  { value: 'createdAt', labelKey: 'toolbar.sortByDate' },
  { value: 'uploadedBy', labelKey: 'toolbar.sortByUploader' },
];

export function ProjectFilesToolbar({
  breadcrumbLabel,
  onNavigateRoot,
  search,
  onSearch,
  viewMode,
  onViewModeChange,
  sortBy,
  sortDir,
  onSortByChange,
  onSortDirToggle,
  onUpload,
  onNewFolder,
  isUploading,
  isInsideFolder,
}: ProjectFilesToolbarProps) {
  const { t } = useTranslation('projectFiles');
  const { toast } = useToast();

  const SortDirIcon = sortDir === 'asc' ? ArrowUp : ArrowDown;

  return (
    <div className="flex items-center gap-3 border-b px-4 py-2" data-testid="project-files-toolbar">
      {/* Breadcrumb */}
      <div className="flex min-w-0 items-center gap-1 text-sm">
        <button
          type="button"
          onClick={onNavigateRoot}
          className={cn(
            'shrink-0 transition-colors',
            isInsideFolder
              ? 'text-muted-foreground hover:text-foreground cursor-pointer'
              : 'text-muted-foreground cursor-default',
          )}
          tabIndex={isInsideFolder ? 0 : -1}
        >
          {t('sidebar.documents')}
        </button>
        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-medium">{breadcrumbLabel}</span>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-1.5 ml-auto">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('toolbar.search')}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="h-8 w-44 pl-8 text-sm"
            data-testid="project-files-toolbar-search"
          />
        </div>

        {/* Sort controls */}
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1 text-xs"
                  data-testid="project-files-toolbar-sort-by"
                  aria-label={t('toolbar.sortBy')}
                >
                  <ArrowUpDown className="size-3.5" />
                  {t(SORT_FIELDS.find((f) => f.value === sortBy)?.labelKey ?? 'toolbar.sortByName')}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('toolbar.sortBy')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            {SORT_FIELDS.map((field) => (
              <DropdownMenuCheckboxItem
                key={field.value}
                checked={sortBy === field.value}
                onCheckedChange={() => onSortByChange(field.value)}
              >
                {t(field.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onSortDirToggle}
              data-testid="project-files-toolbar-sort-dir"
              aria-label={sortDir === 'asc' ? t('toolbar.sortAsc') : t('toolbar.sortDesc')}
            >
              <SortDirIcon className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {sortDir === 'asc' ? t('toolbar.sortAsc') : t('toolbar.sortDesc')}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              className="size-8"
              onClick={() => onViewModeChange('list')}
              data-testid="project-files-toolbar-view-list"
              aria-label={t('toolbar.viewList')}
            >
              <List className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('toolbar.viewList')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon"
              className="size-8"
              onClick={() => onViewModeChange('grid')}
              data-testid="project-files-toolbar-view-grid"
              aria-label={t('toolbar.viewGrid')}
            >
              <LayoutGrid className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('toolbar.viewGrid')}</TooltipContent>
        </Tooltip>

        <Button
          variant="outline"
          size="sm"
          onClick={onNewFolder}
          data-testid="project-files-toolbar-new-folder"
        >
          <FolderPlus className="size-4 mr-1" />
          {t('toolbar.newFolder')}
        </Button>

        <FileUploader
          multiple={false}
          maxSize={100 * 1024 * 1024}
          onFilesAccepted={(files) => files[0] && onUpload(files[0])}
          onFilesRejected={(rejections) => {
            const code = rejections[0]?.errors[0]?.code;
            const titleKey =
              code === 'file-too-large'
                ? 'apiErrors.uploadFileTooLarge'
                : code === 'file-invalid-type'
                  ? 'apiErrors.uploadInvalidType'
                  : 'apiErrors.uploadFailed';
            toast.error({ title: t(titleKey) });
          }}
        >
          {({ getInputProps, open }) => (
            <>
              <input {...getInputProps()} />
              <Button
                size="sm"
                onClick={open}
                disabled={isUploading}
                data-testid="project-files-toolbar-upload"
              >
                <Upload className="size-4 mr-1" />
                {t('toolbar.upload')}
              </Button>
            </>
          )}
        </FileUploader>
      </div>
    </div>
  );
}
